import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { citySuggest } from '@/api/base';
import { Autocomplete } from '@/components/kit/Autocomplete';
import { TextField } from '@/components/kit/Field';
import { FieldHint, OptionList } from '@/components/kit/OptionList';

export interface CityValue {
  id: string;
  name: string;
}

/**
 * Город — только из справочника (БЛ M2 5.13): начинают вводить, система
 * предлагает варианты, выбирают один. Свободный ввод не сохраняется.
 *
 * В модальном окне поповер нельзя (CLAUDE.md), поэтому `inModal`
 * показывает варианты списком под полем; вне окна — Autocomplete кита.
 */
export function CityField({
  label = 'Город',
  value,
  onChange,
  inModal,
  hint,
  error,
}: {
  label?: string;
  value: CityValue | null;
  onChange: (value: CityValue | null) => void;
  inModal?: boolean;
  hint?: string;
  error?: string | null;
}) {
  if (!inModal) {
    return (
      <Autocomplete
        label={label}
        hint={hint}
        error={error}
        value={value ? { value: value.id, label: value.name } : null}
        onChange={(s) => onChange(s ? { id: s.value, name: s.label } : null)}
        suggest={async (q) => (await citySuggest(q)).map((c) => ({ value: c.id, label: c.name, hint: c.region }))}
      />
    );
  }
  return <CityInModal label={label} value={value} onChange={onChange} hint={hint} error={error} />;
}

function CityInModal({
  label,
  value,
  onChange,
  hint,
  error,
}: {
  label: string;
  value: CityValue | null;
  onChange: (value: CityValue | null) => void;
  hint?: string;
  error?: string | null;
}) {
  const [text, setText] = useState(value?.name ?? '');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setQ(text), 200);
    return () => window.clearTimeout(t);
  }, [text]);
  const picking = text.trim().length >= 2 && text !== value?.name;
  const found = useQuery({
    queryKey: ['cities', q],
    queryFn: () => citySuggest(q),
    enabled: picking && q.trim().length >= 2,
  });
  const options = (found.data ?? []).slice(0, 5);

  return (
    <>
      <TextField
        label={label}
        placeholder="начните вводить"
        value={text}
        hint={hint}
        error={error}
        onValue={(v) => {
          setText(v);
          if (value) onChange(null);
        }}
      />
      {picking ? (
        options.length ? (
          <OptionList
            value={null}
            onChange={(id) => {
              const c = options.find((o) => o.id === id);
              if (!c) return;
              onChange({ id: c.id, name: c.name });
              setText(c.name);
            }}
            options={options.map((c) => ({ value: c.id, title: c.name, description: c.region }))}
          />
        ) : found.isFetching ? null : (
          <FieldHint gap>Такого города нет в справочнике.</FieldHint>
        )
      ) : null}
    </>
  );
}
